import { getActionRecommendations } from './sharingTipIdMaps/getActionRecommendations';
import { getCustomActions } from './sharingTipIdMaps/getCustomActions';
import { IdType, SharingData } from '../schema/SharingData';
import { SharingTipId } from '../types/sharingTipId';
import { SharingTipInfo } from '../types/SharingTipInfo';
import { SharingTipRecipientInfo } from '../types/SharingTipRecipientInfo';
import { getInfoBarMessageRank } from '../utils/sharingTipIdMaps/getInfoBarMessageRank';
import { getIssueDetector } from '../utils/sharingTipIdMaps/getIssueDetector';
import { getSharingTipActions } from '../utils/sharingTipIdMaps/getSharingTipActions';
import { isSharingTipEnabled } from '../utils/sharingTipIdMaps/isSharingTipEnabled';
import { sharingTipIdToInfoBarString } from '../utils/sharingTipIdMaps/sharingTipIdToInfoBarString';
import { isStringNullOrWhiteSpace } from 'owa-strings';
import { format } from 'owa-strings';
import { trace } from 'owa-trace';
import {
    InfoBarCustomAction,
    InfoBarMessageRank,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';

export function getSharingTipInfo(
    sharingData: SharingData[],
    sharingTipId: SharingTipId,
    recipientInfo: SharingTipRecipientInfo[],
    convertCloudyToClassicAction?: (id: IdType) => InfoBarCustomAction
): SharingTipInfo | null {
    if (!isSharingTipEnabled(sharingTipId)) {
        return null;
    }
    const sharingTipIssueDetector: (
        sharingData: SharingData,
        recipientInfo: SharingTipRecipientInfo[]
    ) => boolean = getIssueDetector(sharingTipId);
    const sharingDataWithIssue: SharingData[] = sharingData.filter(data =>
        sharingTipIssueDetector(data, recipientInfo)
    );

    if (sharingDataWithIssue.length === 0) {
        return null;
    }

    const fileNames = sharingDataWithIssue
        .filter(x => !isStringNullOrWhiteSpace(x.name))
        .map(x => x.name)
        .join(', ');

    const infoBarStringTemplates = sharingTipIdToInfoBarString(
        sharingTipId,
        recipientInfo,
        sharingDataWithIssue
    );
    const infoBarStringTemplate: string =
        sharingDataWithIssue.length > 1
            ? infoBarStringTemplates.multipleFiles
            : infoBarStringTemplates.singleFile;

    if (
        (!fileNames || fileNames.length === 0) &&
        infoBarStringTemplate &&
        infoBarStringTemplate.length !== 0
    ) {
        trace.error('file names should not be null or empty if the infobarStringTemplate is not');
    }

    const customActions: InfoBarCustomAction[] = getCustomActions(
        sharingDataWithIssue,
        sharingTipId,
        convertCloudyToClassicAction
    );

    const sharingTipActions = getSharingTipActions(sharingTipId);

    let sharingTipString: string | null;
    if (customActions !== null && customActions.length !== 0) {
        sharingTipString = format(infoBarStringTemplate, fileNames);
    } else {
        // If we don't have a custom action that can fix the problem then we append a string recommending a fix to the user, if possible.
        let actionString = '';
        const actionRecommendation = getActionRecommendations(sharingTipId);
        if (actionRecommendation !== null) {
            actionString =
                sharingDataWithIssue.length > 1
                    ? actionRecommendation.multipleFiles
                    : actionRecommendation.singleFile;
        }
        sharingTipString = format(infoBarStringTemplate, fileNames, actionString);
    }

    const messageRank: InfoBarMessageRank = getInfoBarMessageRank(sharingTipId);

    return {
        id: sharingTipId,
        sharingTipString: sharingTipString,
        actions: customActions,
        sharingTipActions: sharingTipActions,
        messageRank: messageRank,
    };
}
